<template>
    <b-tabs
        class="apprentice-pills-tab group-show-settings-tab"
        v-model="activeTab"
        pills
        vertical
        nav-wrapper-class="col-12 col-lg-3 col-xl-2 d-none d-lg-block"
        content-class="col-12 col-lg-9 col-xl-10 group-show-tools-tab-content"
        @input="updateTabIndex">
        <template
            v-slot:tabs-start
            v-if="[2, 6, 7].includes(group.type_id)">
            <b-nav-item
                class="settings-tab-item"
                href="#"
                role="presentation"
                @click.prevent="goToPortfolio"
                link-classes="settings-tab-item-link">
                <div
                    class="tab-title"
                    v-if="group.type_id === 2">
                    <div
                        class="title"
                        v-if="group.permissions.can_edit_portfolio">
                        <i class="fas fa-wrench fa-fw"></i>{{ $t('group.edit_portfolio') }}
                    </div>
                    <div
                        class="title"
                        v-else>
                        <i class="fas fa-wrench fa-fw"></i>{{ $t('group.show_portfolio') }}
                    </div>
                </div>
                <div
                    class="tab-title"
                    v-if="group.type_id === 6">
                    <div
                        class="title"
                        v-if="group.permissions.can_edit_portfolio">
                        <i class="fas fa-wrench fa-fw"></i>{{ $t('group.edit_internship_exercises') }}
                    </div>
                    <div
                        class="title"
                        v-else>
                        <i class="fas fa-wrench fa-fw"></i>{{ $t('group.show_internship_exercises') }}
                    </div>
                </div>
                <div
                    class="tab-title"
                    v-if="group.type_id === 7">
                    <div class="title"><i class="fas fa-wrench fa-fw"></i>{{ $t('group.show.exercises') }}</div>
                </div>
            </b-nav-item>
        </template>

        <div class="group-show-settings-sub-item-nav with-border d-flex d-lg-none">
            <b-form-select
                class="apprentice-primary-select"
                v-model="activeTab"
                :options="options">
            </b-form-select>
        </div>

        <b-tab
            :title="$t('group.options')"
            title-item-class="group-show-settings-tab-item"
            title-link-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-tools fa-fw"></i>{{ $t('group.options') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-options-component
                    :group-in="group"
                    :roadmaps-in="roadmapsIn"
                    :ratings-in="ratingsIn"
                    :questionnaires-in="questionnairesIn"
                    @groupUpdated="updateGroupSetting"
                    :group-options-availability="groupOptionsAvailability">
                </design-2021-group-options-component>
            </div>
        </b-tab>
        <b-tab
            v-if="groupIn.type_id === 6"
            :title="$t('group.internship_list')"
            title-item-class="group-show-internship-types-tab-item"
            title-link-class="group-show-internship-types-tab-item-link"
            @click="getInternships">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-laptop fa-fw"></i>{{ $t('group.internship_list') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-internship-types-component
                    :group-in="groupIn"
                    ref="internship-types">
                </design-2021-group-internship-types-component>
            </div>
        </b-tab>
        <b-tab
            v-if="groupIn.type_id === 6"
            :title="$t('group.internship_agreement')"
            title-item-class="group-show-internship-agreement-tab-item"
            title-link-class="group-show-internship-types-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title">
                        <i class="fas fa-file-contract fa-fw"></i>{{ $t('group.internship.agreement') }}
                    </div>
                </div>
            </template>
            <design-2021-group-internship-agreement-component
                :internship-agreement-in="groupIn.internship_agreement"
                :group-in="groupIn">
            </design-2021-group-internship-agreement-component>
        </b-tab>

        <b-tab
            v-if="groupIn.type_id !== 6 && groupIn.type_id !== 2"
            :title="$t('group.groups')"
            title-item-class="group-show-settings-tab-item"
            title-link-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-users fa-fw"></i>{{ $t('group.groups') }}</div>
                </div>
            </template>

            <div
                class="apprentice-simple-card option-enabled-on-subgroup"
                v-if="isGroupPairEnabledOnParentOrChildGroup">
                <div class="not-found with-header group-pair-setting-manage-elsewhere">
                    <div class="btn-notice-exclamation-triangle icon"></div>
                    <div
                        class="single-group"
                        v-if="groupNamesWithActiveGroupFeature.length === 1">
                        <p class="alert-notice">
                            {{
                                $t('group.groups_function_is_active_and_is_managed_in_the', {
                                    projectType:
                                        groupIn.children.length === 0
                                            ? $t('group.main_project')
                                            : $t('group.sub_project'),
                                })
                            }}
                            <a
                                class="group-item-link"
                                @click.prevent.stop="openProject(groupNamesWithActiveGroupFeature[0])"
                                >{{ groupNamesWithActiveGroupFeature[0].name }}</a
                            >
                        </p>
                    </div>
                    <div
                        class="multiple-groups"
                        v-if="groupNamesWithActiveGroupFeature.length > 1">
                        <p class="alert-notice">
                            {{
                                $t('group.groups_function_is_active_and_managed_in_the', {
                                    projectType:
                                        groupIn.children.length === 0
                                            ? $t('group.main_project')
                                            : $t('group.sub_project'),
                                })
                            }}
                        </p>
                        <ul class="list-unstyled groups">
                            <li
                                class="group-item"
                                v-for="(group, index) of groupNamesWithActiveGroupFeature"
                                :key="index">
                                <a
                                    class="group-item-link"
                                    @click.prevent.stop="openProject(group)"
                                    >{{ group.name }}</a
                                >
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div v-else-if="showGroupPairOptions">
                <design-2021-group-management-component
                    :group-in="group"
                    :courses-in="coursesIn"></design-2021-group-management-component>
            </div>
            <div v-else>
                <div class="apprentice-simple-card files-management">
                    <div
                        class="not-found with-header empty-admin-users-list"
                        v-if="groupPairToolAllowed">
                        <div
                            class="btn-add-circle icon"
                            @click="openGroupPairOptionsField"></div>

                        <div class="alert-notice">
                            {{ $t('group.click_here_to_start_with_the_group_making_tool') }}
                        </div>
                    </div>
                    <div
                        class="not-found with-header empty-admin-users-list"
                        v-else>
                        <div class="btn-alarm-exclamation icon"></div>
                        <div class="alert-notice">
                            {{
                                $t(
                                    'group.in_the_portfolio_here_on_the_main_or_subgroup_you_have_the_collaboration_tool_enabled_somewhere_this_cannot_be_combined_with_the_groups_making_tool',
                                )
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </b-tab>

        <b-tab
            :title="$t('group.administrator')"
            title-item-class="group-show-settings-tab-item"
            title-link-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-user fa-fw"></i>{{ $t('group.administrator') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-admin-component :group-in="group"></design-2021-group-admin-component>
            </div>
        </b-tab>

        <b-tab
            v-if="group.type_id === 4"
            :title="$t('group.page_rights')"
            title-item-class="group-show-settings-tab-item"
            title-link-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-lock fa-fw"></i>{{ $t('group.page_rights') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-pagerights-component :group-in="group"></design-2021-group-pagerights-component>
            </div>
        </b-tab>

        <b-tab
            v-if="hasLas"
            :title="$t('group.connect_classes')"
            title-item-class="group-show-settings-tab-item"
            title-link-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-link fa-fw"></i>{{ $t('group.connect_classes') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-show-link-component :group-in="group"></design-2021-group-show-link-component>
            </div>
        </b-tab>

        <b-tab
            :title="$t('group.upload_banner')"
            title-item-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-image fa-fw"></i>{{ $t('group.upload_banner') }}</div>
                </div>
            </template>

            <div>
                <design-2021-group-custom-banner-component
                    :group-in="group"></design-2021-group-custom-banner-component>
            </div>
        </b-tab>

        <b-tab
            v-if="groupIn.custom_editor_files"
            :title="$t('group.add_extra_work_files')"
            title-item-class="group-show-settings-tab-item-link">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-file-alt fa-fw"></i>{{ $t('group.extra_work_files') }}</div>
                </div>
            </template>

            <div>
                <design-2021-custom-editor-files-component
                    :group-in="group"></design-2021-custom-editor-files-component>
            </div>
        </b-tab>

        <b-tab
            v-if="$authUser.isAdmin() && group.parent_id === null" :title="$t('admin.analytics')">
            <template v-slot:title>
                <div class="tab-title">
                    <div class="title"><i class="fas fa-analytics fa-fw"></i>{{ $t('admin.analytics') }}</div>
                </div>
            </template>
            <div class="apprentice-simple-card">
                <design-2021-analytics-group-settings-component
                    :in-group="true"
                    :group-in="group">
                </design-2021-analytics-group-settings-component>
            </div>
        </b-tab>
    </b-tabs>
</template>

<script>
export default {
    name: 'design-2021-group-settings-component',
    props: [
        'activeTabIn',
        'groupIn',
        'roadmapsIn',
        'ratingsIn',
        'groupOptionsAvailability',
        'questionnairesIn',
        'hasLas',
        'coursesIn',
    ],
    data() {
        return {
            activeTab: this.activeTabIn,
            options: [],
            group: this.groupIn,
            groups: [],
            groupNamesWithActiveGroupFeature: [],
            openGroupPairOptions: false,
        };
    },

    methods: {
        updateGroupSetting(data) {
            this.group[data.option] = data.value;
        },
        updateTabIndex() {
            const name = `session-${this.$authUser.id}-group-${this.group.id}-SettingsTab`;
            localStorage.setItem(name, this.activeTab);
        },

        goToPortfolio() {
            window.location.href =
                this.routes.portfolio() + '/edit/' + this.group.portfolios[0].id + '/' + this.group.id;
        },

        updateSelection(value) {
            if (value === null) {
                this.goToPortfolio();
            }
        },
        showGroup() {
            window.location.href = this.routes.groupShow(this.activeGroupPairGroup.id);
        },

        openProject(project) {
            const setting = `session-${this.$authUser.id}-group-${project.id}-SettingsTab`;
            const main = `session-${this.$authUser.id}-group-${project.id}-MainTab`;
            localStorage.setItem(main, 2);
            localStorage.setItem(setting, 1);
            window.location.href = this.routes.groupShow(project.id);
        },

        getInternships() {
            if (this.groupIn.type_id === 6) {
                this.$refs['internship-types'].show();
            }
        },
        openGroupPairOptionsField() {
            this.openGroupPairOptions = true;
            this.alert.notice(
                this.$t('group.watch_out'),
                this.$t('group.you_must_click_save_at_the_bottom_before_the_group_creation_tool_is_actually_enabled'),
            );
        },
    },
    mounted() {
        if (this.activeTabIn === 1 && this.group.type_id === 6) {
            this.getInternships();
        }
    },
    created() {
        const optionPositions = [this.$t('group.options'), this.$t('group.groups'), this.$t('group.administrator')];
        if (this.group.type_id === 4) {
            optionPositions.push(this.$t('group.page_rights'));
        }

        if (this.hasLas) {
            optionPositions.push(this.$t('group.connect_classes'));
        }

        optionPositions.push(this.$t('group.upload_banner'));

        this.options = optionPositions.map((pos, index) => {
            return {
                value: index,
                text: pos,
            };
        });

        this.options.unshift({ value: null, text: this.$t('group.edit_portfolio') });

        this.axios.get(this.routes.getGroupChildGroupPairStatus(this.groupIn.id)).then(
            (response) => {
                if (response.data) {
                    this.groups = response.data;
                }
            },

            (error) => {},
        );
    },

    computed: {
        isGroupPairEnabledOnParentOrChildGroup() {
            let groupToolEnabled = false;

            if (this.groupIn.group_pair_options) {
                return groupToolEnabled;
            }

            if (this.groupIn.children.length === 0) {
                for (const parent of this.groupIn.parents) {
                    if (parent.group_pair_options) {
                        this.groupNamesWithActiveGroupFeature.push({
                            id: parent.id,
                            name: parent.name,
                        });
                        groupToolEnabled = true;
                    }
                }
            } else {
                for (const child of this.groupIn.children) {
                    if (child.group_pair_options) {
                        this.groupNamesWithActiveGroupFeature.push({
                            id: child.id,
                            name: child.name,
                        });
                        groupToolEnabled = true;
                    }
                }
            }

            return groupToolEnabled;
        },
        groupPairToolAllowed() {
            if (this.groupIn.assignment_cooperations.length) {
                return false;
            }
            for (const child of this.groupIn.children) {
                if (child.assignment_cooperations.length) {
                    return false;
                }
            }
            for (const parent of this.groupIn.parents) {
                if (parent.assignment_cooperations.length) {
                    return false;
                }
            }
            return true;
        },
        showGroupPairOptions() {
            return this.group.group_pair_options || this.openGroupPairOptions;
        },
    },
};
</script>

<style scoped lang="scss">
.settings-tab ::v-deep {
    .nav-item {
        .nav-link {
            display: flex;
            align-items: center;
        }

        .title {
            line-height: 1.2;
        }
    }
}

.group-show-tools-sub-item-nav {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    display: flex;
    align-items: center;
    justify-content: flex-end;

    select {
        width: auto;
        border: 1px solid var(--primary);
        margin-bottom: 0;
    }

    .btn-edit-portfolio {
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
        align-self: center;
        flex: 0 0 auto;
    }
}

.empty-admin-users-list {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .icon {
        &:before {
            margin-right: 0;
        }
    }
}

.group-pair-setting-manage-elsewhere {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    height: auto;

    .alert-notice {
        color: $text-color;
    }

    .active-group {
        &:hover {
            cursor: pointer;
        }
    }
}

.groups {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
}

.group-item {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.group-item-link {
    font-size: 1.125rem;
    line-height: 1.2;
    cursor: pointer;
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .group-pair-setting-manage-elsewhere {
        .notice-text {
            max-width: 75%;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .group-pair-setting-manage-elsewhere {
        .notice-text {
            max-width: 50%;
        }
    }
}
</style>
